import { t } from '@exam-prep/i18n';
import { Button, Card, CardHeader, CardTitle, CardDescription, CardContent } from '@exam-prep/ui';
import { useLoad } from '../../lib/useLoad';
import { when } from '../../lib/format';
import { api } from '../../lib/api';
import type { Hub as HubData } from '../../lib/api-types';
import { ErrorBox, Loading } from '../../components/Feedback';
import { BrandBar } from '../../components/BrandBar';
import type { Props } from './model/hub';
import { mockSlot } from './model/mocks-available';
import { GrandMockHubCard } from '../grand-mock';
import { MockLibrary, MockRow, MyResults } from './ui/Hub';
import { PlansCard } from '../plans';

export function Hub({ onOpenMock, onResume, onResult, onGrandMock, onCards }: Props) {
  const { data, error, reload } = useLoad(() => api<HubData>('GET', '/app/hub'), []);
  if (error) return <ErrorBox error={error} retry={reload} />;
  if (!data) return <Loading />;
  const live = data.live;
  return (
    <div className="pb-6">
      <BrandBar />
      <h1 className="px-4 pt-5 pb-1 font-display text-2xl font-bold">
        <span className="brand-accent-text">
          {t(data.mocksEnabled ? 'miniapp.hub.hello' : 'miniapp.hub.hello_no_mocks')}
        </span>
      </h1>
      <div className="space-y-4 p-4">
        {data.activeAttemptId ? (
          <Button
            size="lg"
            className="w-full"
            onClick={() => onResume(String(data.activeAttemptId))}
          >
            {t('miniapp.hub.resume')}
          </Button>
        ) : null}

        {data.mocksEnabled ? <GrandMockHubCard onOpen={onGrandMock} /> : null}

        <Button variant="outline" size="lg" className="w-full" onClick={onCards}>
          🗂 Flashcards
        </Button>

        {mockSlot(data) === 'off' ? null : mockSlot(data) === 'empty' ? (
          <Card>
            <CardContent className="pt-4">
              <p role="status" className="text-sm text-muted-foreground">
                {t('miniapp.hub.mocks_empty')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="live-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className="inline-block size-2.5 rounded-full bg-[var(--brand-danger)] shadow-[0_0_12px_var(--brand-danger)]"
                  />
                  {t('miniapp.hub.live')}
                </CardTitle>
                <CardDescription>
                  {live
                    ? live.myAttempt
                      ? t('miniapp.hub.live_done', { at: when(live.mock.resultsAt) })
                      : t('miniapp.hub.live_window', { end: when(live.mock.windowEnd) })
                    : data.nextLive
                      ? t('miniapp.hub.live_next', { start: when(data.nextLive.windowStart) })
                      : t('miniapp.hub.live_none')}
                </CardDescription>
              </CardHeader>
              {live ? (
                <CardContent>
                  {live.myAttempt ? (
                    <Button variant="outline" onClick={() => onResult(String(live.myAttempt?.id))}>
                      {t('miniapp.hub.results')}
                    </Button>
                  ) : (
                    <MockRow m={live.mock} onOpen={() => onOpenMock(live.mock.id)} />
                  )}
                </CardContent>
              ) : null}
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('miniapp.hub.library')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <MockLibrary library={data.library} onOpen={onOpenMock} />
              </CardContent>
            </Card>
          </>
        )}

        {data.mocksEnabled ? <MyResults results={data.results} onResult={onResult} /> : null}

        <PlansCard />
      </div>
    </div>
  );
}
